// Board salary review — projection engine.
// Pure functions over the ETL dataset (api/utils/boardReviewData.json).
// Everything the board page shows is project(data, scenario); no UI coupling.
//
// Salary levers are PER GROUP, expressed as "% package increase vs today's
// staff on a full year of the current (2026) scales". The proposed structure
// is a different % for each group (Teacher +14.5% … Leadership +46.6%); the
// global salary slider is the package-weighted average and scales every group
// proportionally. New-2027 hires with no 2026 baseline plus workers comp form
// a small constant unaffected by the levers.
//
// Other levers (all %):
//   feeIncreasePct      school fee increase applied in 2027 (proposal: 0%).
//   costInflationPct    annual growth of staffing + operating costs from 2028 on.
//   feeIndexationPct    annual growth of tuition/trading/other income from 2028 on.
//   partnershipRevenue  extra annual revenue (VUV) from the partnership program,
//                       starting 2027, indexed with feeIndexationPct.
//
// 2025 and 2026 are reported as-is from the workbook. 2027 is the decision
// year. 2028+ is the app's own projection from the 2027 base — the workbook's
// out-years revert to pre-increase staffing and are not usable.

const YEAR_DECISION = 2027;

// Groups with a usable 2026 baseline (matched staff).
export function leverGroups(data) {
  return data.groups.filter((g) => g.matched > 0 && g.package2026 > 0);
}

// Package cost of levered groups at current scales / at the proposal.
function groupBase(data) {
  return leverGroups(data).reduce((s, g) => s + g.package2026, 0);
}
function groupProposed(data) {
  return leverGroups(data).reduce((s, g) => s + g.package2027Matched, 0);
}

// New hires without a 2026 baseline: constant under the levers. (Workers comp
// is folded into the Ancillary group by the ETL.)
function leverConstant(data) {
  const allNew = data.groups.reduce((s, g) => s + g.package2027, 0);
  return allNew - groupProposed(data);
}

// Proposed uplift % per group (the "reset" position of each group lever).
export function proposedGroupPct(data) {
  const out = {};
  for (const g of leverGroups(data)) {
    out[g.group] = (g.package2027Matched / g.package2026 - 1) * 100;
  }
  return out;
}

// Package-weighted average of a set of group percentages.
export function averageGroupPct(data, groupPct) {
  const gs = leverGroups(data);
  const base = groupBase(data);
  return gs.reduce((s, g) => s + g.package2026 * (groupPct[g.group] || 0), 0) / base;
}

export function proposedSalaryIncreasePct(data) {
  return averageGroupPct(data, proposedGroupPct(data));
}

// Headline the board also knows: % increase of the staffing package pool vs
// the 2026 *budget* (which already contains one term of the new structure).
export function packagePool(data, groupPct) {
  const gs = leverGroups(data);
  return (
    gs.reduce((s, g) => s + g.package2026 * (1 + (groupPct[g.group] || 0) / 100), 0) +
    leverConstant(data)
  );
}

// Partnership revenue already embedded in the workbook's 2027 income
// (40 partner students from 2027; confirmed by the finance team, 05.08.26).
export const BASELINE_PARTNERSHIP_REVENUE = 40_000_000;

export function defaultScenario(data) {
  return {
    groupIncreasePct: proposedGroupPct(data),
    feeIncreasePct: 0,
    enrolmentPct: 0,
    feeElasticity: 0, // % of enrolment lost per 1% fee increase
    costInflationPct: 3,
    feeIndexationPct: 3,
    partnershipEnabled: true,
    partnershipRevenue: 40_000_000,
    // Possible future scale steps (not agreed) — chart projection only; the
    // band tables always show the step-1 (2027) rates.
    futureSteps: [
      { key: 'step2', label: 'Step 2', year: 2029, pct: 10, enabled: false },
      { key: 'step3', label: 'Step 3', year: 2031, pct: 10, enabled: false },
    ],
    // New-classroom simulation (up to 3). Priced at max cost (one CAT 1b
    // teacher + one average TA) and 75%-capacity revenue at local fees.
    classrooms: [
      { key: 'c1', label: 'Year 7–8 classroom', type: 'y78', enabled: false, year: 2028 },
      { key: 'c2', label: 'Year 1–2 classroom', type: 'y12', enabled: false, year: 2028 },
      { key: 'c3', label: 'Any additional class', type: 'y78', enabled: false, year: 2028 },
    ],
    horizonYear: 2033,
  };
}

// Classroom simulation economics, 2027 VUV.
// Revenue: 75% of room capacity at that level's local fee (Y7–8: 24 of 32 at
// 731,641; Y1–2: 25 of 33 at 696,367). Cost: max-cost staffing — one CAT 1b
// teacher plus one average teaching assistant.
export const CLASSROOM_TYPES = {
  y78: { students: 24, capacity: 32, fee2027: 731641.05 },
  y12: { students: 25, capacity: 33, fee2027: 696367.35 },
};

export function classroomCost2027(data) {
  const cat1b = Math.max(
    ...data.bands.staff2027.filter((b) => /^teacher$/i.test(b.position.trim())).map((b) => b.package),
  );
  const ta = data.groups.find((g) => g.group === 'Teaching Assistant');
  const avgTa = ta ? ta.package2027 / ta.headcount : 0;
  return cat1b + avgTa;
}

// Scale every group lever proportionally so the weighted average hits avgPct.
export function scenarioWithAveragePct(data, scenario, avgPct) {
  const cur = averageGroupPct(data, scenario.groupIncreasePct);
  const groupPct = {};
  if (Math.abs(cur) < 1e-9) {
    for (const g of leverGroups(data)) groupPct[g.group] = avgPct;
  } else {
    const k = avgPct / cur;
    for (const g of leverGroups(data)) {
      groupPct[g.group] = (scenario.groupIncreasePct[g.group] || 0) * k;
    }
  }
  return { ...scenario, groupIncreasePct: groupPct };
}

function line(data, name, year) {
  const i = data.financials.years.indexOf(year);
  return data.financials.lines[name][i];
}

export function project(data, scenario) {
  const s = data.staffing;
  const out = [];

  // Months-of-salary-in-cash for the reported years (KPIs tab; 2026 recomputed
  // on the corrected 05.08.26 baseline: 30.87M bank / 21.03M monthly salaries).
  const REPORTED_MONTHS_CASH = { 2025: 1.47, 2026: 1.47 };

  for (const year of [2025, 2026]) {
    const staffing = line(data, 'staffingExp', year);
    const totalIncome = line(data, 'totalIncome', year);
    out.push({
      year,
      kind: year === 2025 ? 'actual' : 'budget',
      tuitionIncome: line(data, 'tuitionIncome', year),
      totalIncome,
      staffing,
      totalExpenses: line(data, 'totalExpenses', year),
      operatingResult: line(data, 'operatingResult', year),
      staffPct: staffing / totalIncome,
      monthsCash: REPORTED_MONTHS_CASH[year],
    });
  }

  const feeMult = 1 + scenario.feeIncreasePct / 100;
  // Effective enrolment change: the lever, less any fee-driven attrition.
  const effEnrolPct = (scenario.enrolmentPct || 0) - (scenario.feeElasticity || 0) * scenario.feeIncreasePct;
  const enrolMult = Math.max(0, 1 + effEnrolPct / 100);
  const infl = 1 + scenario.costInflationPct / 100;
  const idx = 1 + scenario.feeIndexationPct / 100;
  // The 2027 baseline ALREADY includes the partnership revenue (board model
  // 05.08.26), so the lever is a delta against that baseline amount: on at
  // 40M = the model as-is; off = baseline minus 40M.
  const partnership =
    (scenario.partnershipEnabled ? scenario.partnershipRevenue : 0) - BASELINE_PARTNERSHIP_REVENUE;

  const base = {
    tuition: line(data, 'tuitionIncome', YEAR_DECISION),
    trading: line(data, 'tradingIncome', YEAR_DECISION),
    other: line(data, 'otherIncome', YEAR_DECISION),
    tuitionExp: line(data, 'tuitionExp', YEAR_DECISION),
    propertyExp: line(data, 'propertyExp', YEAR_DECISION),
    adminExp: line(data, 'adminExp', YEAR_DECISION),
    tradingExp: line(data, 'tradingExp', YEAR_DECISION),
    otherExp: line(data, 'otherExp', YEAR_DECISION),
    interest: line(data, 'interestExp', YEAR_DECISION),
    depreciation: line(data, 'depreciationExp', YEAR_DECISION),
  };

  const pool2027 = packagePool(data, scenario.groupIncreasePct);
  const steps = scenario.futureSteps || [];
  const classrooms = (scenario.classrooms || []).filter((c) => c.enabled);
  const classCost27 = classrooms.length ? classroomCost2027(data) : 0;

  // Simplified cash roll-forward: closing 2026 bank, then operating result
  // + depreciation (non-cash) − loan repayments; the workbook's one-off 2027
  // balance-sheet movement is carried as given.
  const f = data.financials;
  let cash = f.openingBank2027 || 0;

  for (let year = YEAR_DECISION; year <= scenario.horizonYear; year++) {
    const n = year - YEAR_DECISION;
    const growCost = Math.pow(infl, n);
    const growIncome = Math.pow(idx, n);

    // New classrooms active this year: fee revenue in, staff cost out. Both
    // are 2027-priced and inherit the fee/inflation levers (and scale steps).
    const active = classrooms.filter((c) => year >= c.year);
    const classRevenue = active.reduce((sum, c) => {
      const t = CLASSROOM_TYPES[c.type];
      return sum + t.students * t.fee2027 * feeMult * growIncome;
    }, 0);

    const tuitionIncome = base.tuition * feeMult * enrolMult * growIncome + classRevenue;
    const totalIncome =
      tuitionIncome + base.trading * growIncome + base.other * growIncome + partnership * growIncome;

    // Future scale steps compound on the package pool (not on overheads).
    const stepMult = steps.reduce(
      (m, st) => (st.enabled && year >= st.year ? m * (1 + st.pct / 100) : m), 1);
    const staffing =
      (pool2027 * stepMult + s.overheads2027) * growCost +
      active.length * classCost27 * stepMult * growCost;
    const oi = data.financials.outYears.indexOf(year);
    const interest = year === YEAR_DECISION ? base.interest : (data.financials.interestOut[oi] ?? 0);
    const depreciation = year === YEAR_DECISION ? base.depreciation : (data.financials.depreciationOut[oi] ?? 0);
    const opex = (base.tuitionExp + base.propertyExp + base.adminExp + base.tradingExp + base.otherExp) * growCost;

    const totalExpenses = staffing + opex + interest + depreciation;
    const operatingResult = totalIncome - totalExpenses;

    const loanRepay = year === YEAR_DECISION
      ? (f.loanRepay2027 || 0)
      : (f.loanRepayOut ? (f.loanRepayOut[oi] ?? 0) : 0);
    cash += operatingResult + depreciation - loanRepay +
      (year === YEAR_DECISION ? (f.balanceSheetMove2027 || 0) : 0);

    out.push({
      year,
      kind: year === YEAR_DECISION ? 'decision' : 'projection',
      tuitionIncome,
      totalIncome,
      staffing,
      totalExpenses,
      operatingResult,
      staffPct: staffing / totalIncome,
      cash,
      monthsCash: cash / (staffing / 12),
    });
  }

  return out;
}

export function yearResult(results, year) {
  return results.find((r) => r.year === year);
}

// ── break-even solvers ───────────────────────────────────────────────────
// Solved numerically (bisection) so the fee-elasticity link is respected.
function solveFee(data, scenario, evalFn) {
  let lo = 0, hi = 30;
  const at = (fp) => evalFn(yearResult(project(data, { ...scenario, feeIncreasePct: fp }), YEAR_DECISION));
  if (at(lo) >= 0) return lo - (at(lo) === 0 ? 0 : 0); // already met at 0%
  if (at(hi) < 0) return hi; // unreachable within range — report the cap
  for (let i = 0; i < 40; i++) {
    const mid = (lo + hi) / 2;
    if (at(mid) < 0) lo = mid; else hi = mid;
  }
  return (lo + hi) / 2;
}

// Fee increase (%) needed to reach `target` operating result in 2027.
export function feeNeededFor(data, scenario, target) {
  return solveFee(data, scenario, (r) => r.operatingResult - target);
}

// Fee increase (%) needed so the 2027 result reaches `marginPct` of income.
export function feeNeededForMargin(data, scenario, marginPct) {
  return solveFee(data, scenario, (r) => r.operatingResult - (marginPct / 100) * r.totalIncome);
}

// Fee increase (%) needed so staffing = `pct` of 2027 income.
export function feeNeededForStaffPct(data, scenario, pct) {
  return solveFee(data, scenario, (r) => r.totalIncome - r.staffing / pct);
}

// Average salary increase (%) affordable while keeping `target` result in 2027.
export function salaryAffordableFor(data, scenario, target) {
  const zero = scenarioWithAveragePct(data, scenario, 0);
  const r0 = yearResult(project(data, zero), YEAR_DECISION);
  return ((r0.operatingResult - target) / groupBase(data)) * 100;
}

// Staffing bill that puts staff cost at `pct` of income (fee lever as-is), and
// its gap to the current scenario's bill.
export function staffingAtPct(data, scenario, pct) {
  const r = yearResult(project(data, scenario), YEAR_DECISION);
  const bill = r.totalIncome * pct;
  return { bill, gap: r.staffing - bill };
}

// ── "Which lever moves the result" — ±1 move on each lever ───────────────
export function leverSensitivity(data, scenario) {
  const tuition27 = line(data, 'tuitionIncome', YEAR_DECISION) * (1 + scenario.feeIncreasePct / 100);
  const gs = leverGroups(data);
  const teacher = gs.find((g) => g.group === 'Teacher');
  const rows = [
    { key: 'enrolment', label: 'Enrolment ±1%', impact: tuition27 / 100, note: 'tuition-proportional; costs held constant' },
    { key: 'fees', label: 'School fees ±1%', impact: line(data, 'tuitionIncome', YEAR_DECISION) / 100, note: 'on 2027 tuition income' },
    { key: 'salary', label: 'Salary scale ±1 pp', impact: -groupBase(data) / 100, note: 'all groups, vs current scales' },
  ];
  if (teacher) {
    rows.push({ key: 'teachers', label: 'Teachers only ±1 pp', impact: -teacher.package2026 / 100, note: 'the largest single group' });
  }
  rows.push({
    key: 'partnership',
    label: `Partnership program (${formatM(scenario.partnershipRevenue)} · in baseline)`,
    impact: scenario.partnershipRevenue,
    oneSided: true,
    note: 'already included in the 2027 model; unticking the lever removes it',
  });
  return rows.sort((a, b) => Math.abs(b.impact) - Math.abs(a.impact));
}

// ── teacher parity (Australia) ───────────────────────────────────────────
export const AUD_PER_VUV = 0.0119;

// Australian resident income tax 2025-26 + 2% Medicare levy.
export function audNetFromGross(gross) {
  let tax = 0;
  if (gross > 190000) tax = 51638 + 0.45 * (gross - 190000);
  else if (gross > 135000) tax = 31288 + 0.37 * (gross - 135000);
  else if (gross > 45000) tax = 4288 + 0.30 * (gross - 45000);
  else if (gross > 18200) tax = 0.16 * (gross - 18200);
  return gross - tax - 0.02 * gross;
}

// CAT 1b (top classroom band): cash salary = base + allowance (comparable to
// an AU gross excl. super); take-home deducts the 6% employee VNPF. Vanuatu
// levies no personal income tax, so take-home ≈ cash − VNPF.
export function teacherTopBand(data) {
  const teachers = data.bands.staff2027.filter((b) => /^teacher$/i.test(b.position.trim()));
  return teachers.reduce((a, b) => (b.package > a.package ? b : a), teachers[0]);
}

export function teacherParity(data) {
  const b = teacherTopBand(data);
  const cash = b.base + b.allowance;
  return {
    band: b.band,
    grossVuv: cash,
    netVuv: cash - 0.06 * b.base,
    grossAud: cash * AUD_PER_VUV,
    netAud: (cash - 0.06 * b.base) * AUD_PER_VUV,
  };
}

export function formatM(v) {
  const m = v / 1e6;
  const digits = Math.abs(m) >= 100 ? 1 : 1;
  return `${m.toLocaleString('en-US', { minimumFractionDigits: digits, maximumFractionDigits: digits })}M`;
}

export function formatVUV(v) {
  return `${Math.round(v).toLocaleString('en-US')} VT`;
}

export function formatPct(x, digits = 1) {
  return `${(x * 100).toFixed(digits)}%`;
}

// Status wording: 65% is the benchmark, 70% the ceiling.
// Good up to the 70% ceiling; warning above it; serious above 75%; critical above 80%.
export function staffPctStatus(pct) {
  if (pct <= 0.70) return { key: 'good', word: 'Within the 70% ceiling', short: 'Within ceiling' };
  if (pct <= 0.75) return { key: 'warning', word: 'Above the 70% ceiling', short: 'Above ceiling' };
  if (pct <= 0.80) return { key: 'serious', word: 'Well above the 70% ceiling', short: 'Well above ceiling' };
  return { key: 'critical', word: 'Critical — above 80% of income', short: 'Critical' };
}
